import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import WizardStepper from '../shared/wizard-stepper';
import { useCreatePlanWizard } from './use-create-plan-wizard';
import { WizardStep1PlanInfo } from './wizard-step1-plan-info';
import { WizardStep2FeaturesPricing } from './wizard-step2-features-pricing';
import { WizardStep3Review } from './wizard-step3-review';

interface CreatePlanWizardProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateFromPlanId?: number;
  currency?: 'EUR' | 'USD';
}

export default function CreatePlanWizard({
  isOpen,
  onClose,
  duplicateFromPlanId,
  currency = 'EUR'
}: CreatePlanWizardProps) {
  const { t } = useTranslation();

  const {
    currentStep,
    setCurrentStep,
    steps,
    totalSteps,
    watchedValues,
    planType,
    register,
    control,
    errors,
    setValue,
    pricePlans,
    resellers,
    setResellerSearchQuery,
    isCreating,
    handleClose,
    handleNext,
    handleBack,
    handleCreate
  } = useCreatePlanWizard({ isOpen, onClose, duplicateFromPlanId });

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WizardStep1PlanInfo
            watchedValues={watchedValues}
            setValue={setValue}
            register={register}
            errors={errors}
          />
        );
      case 2:
        return (
          <WizardStep2FeaturesPricing
            watchedValues={watchedValues}
            planType={planType}
            setValue={setValue}
            register={register}
            control={control}
            errors={errors}
            pricePlans={pricePlans}
          />
        );
      case 3:
        return (
          <WizardStep3Review
            watchedValues={watchedValues}
            planType={planType}
            pricePlans={pricePlans}
            resellers={resellers}
            setResellerSearchQuery={setResellerSearchQuery}
            setValue={setValue}
            currency={currency}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      title={duplicateFromPlanId ? t('duplicate-plan') : t('create-plan')}
      caption={
        duplicateFromPlanId
          ? t('duplicate-plan-wizard-description')
          : t('create-plan-wizard-description')
      }
      footerActions={[
        ...(currentStep > 1
          ? [
              <Button
                key="back"
                onClick={handleBack}
                label={t('back')}
                size="small"
                variant="smoke"
                icon={<ChevronLeft className="h-4 w-4" />}
                disabled={isCreating}
              />
            ]
          : []),
        currentStep < totalSteps ? (
          <Button
            key="next"
            onClick={handleNext}
            label={t('next')}
            size="small"
            variant="primary"
            icon={<ChevronRight className="h-4 w-4" />}
            iconPosition="right"
          />
        ) : (
          <Button
            key="create"
            onClick={handleCreate}
            label={isCreating ? t('creating') : t('create-plan')}
            size="small"
            variant="primary"
            disabled={isCreating}
          />
        )
      ]}
    >
      <div className="pb-2 border-b border-hairline">
        <WizardStepper
          steps={steps}
          currentStep={currentStep}
          onStepClick={setCurrentStep}
          allowNavigation
        />
      </div>

      <div className="max-h-[55vh] overflow-y-auto px-1 pt-2 pb-1">
        {renderStep()}
      </div>
    </Modal>
  );
}
